import { useAuthStore } from '@/store/pinia/auth'
import { isEmpty } from 'lodash'
import createClient, { Middleware } from 'openapi-fetch'
import { APIError } from '../error'
import type { paths } from './api'
import { usePending2FAStore } from '@/store/pinia/pending-2fa'
import { useDeviceCodeStore } from '@/store/pinia/device-code'
import { useAttendanceShareTokenStore } from '@/store/pinia/attendance-share-token'

const client = createClient<paths>({
  baseUrl: process.env.VUE_APP_API_URL,
  credentials: 'include',
  fetch: (input: RequestInfo | URL, init: RequestInit = {}) => {
    return window.fetch(input, { ...init, signal: AbortSignal.timeout(30000) })
  },
  querySerializer: (queryParams) => {
    const query = []
    for (const name in queryParams) {
      const value = queryParams[name]

      if (typeof value === 'object' && !isEmpty(value)) {
        query.push(`${name}=${encodeURIComponent(JSON.stringify(value))}`)
      } else if (typeof value !== 'undefined') {
        query.push(`${name}=${encodeURIComponent(value as string | boolean | number)}`)
      }
    }

    return query.join('&')
  },
})

const interceptors: Middleware = {
  async onRequest({ request }) {
    const authStore = useAuthStore()
    const deviceCodeStore = useDeviceCodeStore()
    const pending2FAStore = usePending2FAStore()
    const attendanceShareTokenStore = useAttendanceShareTokenStore()

    const token = attendanceShareTokenStore.token ?? authStore._token ?? pending2FAStore.token

    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`)
    }

    const deviceCode = authStore.userEmail ? deviceCodeStore.getFromMap(authStore.userEmail) : undefined

    if (deviceCode) {
      request.headers.set('Bioplanner-Device-Code', deviceCode)
    }

    return request
  },
  // onError does not handle error responses with 4xx or 5xx
  async onError({ error }) {
    return new APIError({
      message: APIError.messageFallback,
      description: 'Ocorreu um erro ao acessar a API',
      code: 500,
      cause: error,
    })
  },
}

client.use(interceptors)

export { client }
